import Vue from 'vue';
import axios from 'axios';
import * as api from '@/util/api';

// Login state shared by the whole app.
// The session cookie is the source of truth; localStorage.user is only a hint, so a browser that has
// never signed in can skip the first round trip. status: 'unknown' | 'authed' | 'anon'.
// 'unknown' only exists while the very first check is in flight, and nothing auth-related is rendered then
// (otherwise the header would flash "Sign in" for a moment before switching to "Dashboard").
export const session = Vue.observable({ status: 'unknown', user: '' });

// A session verified less than this long ago is trusted; after that the next route change or tab focus re-checks.
const TRUST_MS = 60000;

let checkedAt = 0;
let inflight = null;
// The hint the current state was derived from: if localStorage changes (another tab signs in or out),
// the cached answer no longer applies and the next check goes to the server.
let lastHint = null;

function apply(authed, user) {
    session.status = authed ? 'authed' : 'anon';
    session.user = authed && user ? user : '';
    checkedAt = Date.now();
    if (authed) {
        if (session.user) window.localStorage.setItem('user', session.user);
    } else {
        window.localStorage.removeItem('user');
    }
    lastHint = window.localStorage.getItem('user');
}

export function isAuthed() {
    return session.status === 'authed';
}

// After "login" and "signup" succeed the server has already answered, so no need to ask again.
export function markAuthed(email) {
    apply(true, email);
}

// After "logout" or any 401 from the API.
export function markAnon() {
    apply(false);
}

// Asks the server whether the cookie is still valid. Sessions expire after 30 minutes of inactivity,
// so localStorage alone keeps the console buttons visible on a dead session — clicking one then only
// shows "you are not signed in". Concurrent callers share a single request.
//
// askServer=true is for console pages (router guard): there the cookie may be valid even though this
// browser has no localStorage hint (cleared site data, another profile), so it is worth one request.
// Anonymous visitors browsing public pages never reach for the network.
export function verify({ askServer = false } = {}) {
    if (inflight) return inflight;
    const hint = window.localStorage.getItem('user');
    if (checkedAt > 0 && Date.now() - checkedAt < TRUST_MS && hint === lastHint) {
        // A fresh "signed in" answer is good for every caller; a fresh "signed out" one is only trusted
        // when nobody is specifically asking the server (console pages pass askServer to be sure).
        if (isAuthed() || !askServer) return Promise.resolve(isAuthed());
    }
    if (!hint && !askServer) {
        apply(false);
        return Promise.resolve(false);
    }
    // Plain axios on purpose: the shared instance answers 401 with a user-facing warning and a redirect,
    // which is wrong for a background check.
    inflight = axios.get(api.checkLogin)
        .then(() => {
            apply(true, session.user || hint);
            return true;
        })
        .catch(() => {
            apply(false);
            return false;
        })
        .then((ok) => {
            inflight = null;
            return ok;
        });
    return inflight;
}

// A session can also expire while the page stays open, so re-check when the user comes back to the tab.
export function watchFocus(onExpired) {
    const recheck = () => {
        if (document.hidden || session.status !== 'authed') return;
        verify().then((ok) => {
            if (!ok && onExpired) onExpired();
        });
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
}

// Another tab may sign in or out; localStorage is the only signal that reaches this tab.
export function watchStorage() {
    window.addEventListener('storage', (event) => {
        if (event.key === 'user') verify({ askServer: true });
    });
}
